import {
  type File,
  type Report,
  ReportStatus,
  useStores,
} from '@littlebird/sdk';
import {
  adaptReportToRoutineRun,
  deviceCount,
  devicesWord,
  findLatestCompletedRoutineRun,
  formatUserWaitTitle,
  getRunUserWait,
  isRunWaiting,
  IMPROVE_ROUTINE_PROMPT,
  selectRunIdAfterDeletion,
} from '@littlebird/ui-utils';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { SHIMMER_TEXT_CLASSES } from '@/components/blocks/Message/reasoning/ShimmerCollapsibleHeader';
import { StaffBackendLabel } from '@/components/blocks/staff-backend-label';
import { IncludeSelectionButton } from '@/components/blocks/Thread/Chat';
import { Thread } from '@/components/blocks/Thread/Thread';
import { IconButton, Text } from '@/components/ui';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Button } from '@/components/ui/Button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Empty } from '@/components/ui/EmptyState';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Skeleton } from '@/components/ui/skeleton';
import { renderPaperChild } from '@/features/artifacts/artifact-paper-content';
import { useArtifactContent } from '@/features/artifacts/useArtifactContent';
import { useCanvasExport } from '@/features/canvas/useCanvasExport';
import { chatRowLayoutStore } from '@/features/chat/chat-row-layout.store';
import { FilePreviewHostContext } from '@/features/chat/file-preview-host';
import { FilePreviewSidebar } from '@/features/chat/FilePreviewSidebar';
import { routineChatActions } from '@/features/cmd-bar/actions/chat';
import useCommandBarActions from '@/features/cmd-bar/use-command-bar-actions';
import { useResumeRoutine } from '@/features/routines/hooks/use-resume-routine';
import { OlderRunsLoader } from '@/features/routines/older-runs-loader';
import {
  type ReportBody,
  useReportBody,
} from '@/features/routines/report-body';
import {
  getRoutineActions,
  getRoutineActionsWithLatestRun,
} from '@/features/routines/routine-action-state';
import {
  formatNoteDate,
  parseStatus,
} from '@/features/routines/routines.utils';
import { useLoadRoutineRuns } from '@/features/routines/use-load-routine-runs';
import { useMarkReportViewed } from '@/features/routines/use-mark-report-viewed';
import {
  hasRoutineReportDetail,
  routineReadState,
  useRoutineRefresh,
  useRoutineReportDetail,
} from '@/features/routines/use-routine-refresh';
import { SandboxTerminalPanel } from '@/features/sprites/SandboxTerminalPanel';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { uiStore } from '@/stores/ui.store';

import { DeviceIcon } from './components/device-icon';
import { RoutineLibrarySection } from './components/routine-library-section';
import { RoutineReportContent } from './components/routine-report-content';
import {
  RoutineContentLoading,
  SkippedDevicesNote,
} from './components/routine-run-entry';
import { useTrackPageViews } from './hooks/use-routine-actions';
import { useSelectedRoutineReport } from './hooks/use-selected-routine-report';
import { RoutineConfigureModal } from './routines-create';
import { routinesStore } from './routines.state';

import type { RoutineChatView } from './routine-chat-view';

type RoutinesProps = {
  routineId: number;
  view?: RoutineChatView;
  requestedReportId?: number;
  onViewChange?: (view: RoutineChatView) => void;
  onFollowingReportChange?: (reportId: number) => void;
};

export const Routines = observer(
  ({
    routineId,
    view = 'canvas',
    requestedReportId,
    onViewChange,
    onFollowingReportChange,
  }: RoutinesProps) => {
    useTrackPageViews();
    void settingsStore.timeFormat;

    const {
      threads: threadsStore,
      reports: reportStore,
      routines: routineStore,
    } = useStores();
    useRoutineRefresh();
    useLoadRoutineRuns(routineId);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [routineRowRef, routineRowRect] = useResizeObserver<HTMLDivElement>();

    useEffect(() => {
      chatRowLayoutStore.setRowWidth(routineRowRect.width);
    }, [routineRowRect.width]);

    const isRoutinesLoading = !routineReadState.hasLoadedRoutines;
    const isReportsLoading = !routineReadState.hasLoadedReports;
    const isRoutinesEmpty =
      !isRoutinesLoading && routineStore.orderedData.length === 0;

    const { routineReports, selectedReport } = useSelectedRoutineReport(
      routineId,
      reportStore.orderedReports,
      requestedReportId
    );

    useEffect(() => {
      if (
        selectedReport?.referenceId &&
        selectedReport.referenceId !== requestedReportId
      ) {
        if (requestedReportId !== undefined) {
          routinesStore.unpinReport(routineId);
        }
        onFollowingReportChange?.(selectedReport.referenceId);
      }
    }, [
      routineId,
      requestedReportId,
      selectedReport?.referenceId,
      onFollowingReportChange,
    ]);
    const isReportDetailPending = useRoutineReportDetail(selectedReport);
    const report = selectedReport;
    const selectedReportBody = useReportBody(report?.artifactFileIds);
    const reportThread = report?.chatId
      ? threadsStore.getById(report.chatId)
      : undefined;
    const [settledThreadId, setSettledThreadId] = useState<string | null>(null);
    const shouldShowContentLoader = isReportDetailPending;

    useEffect(() => {
      const threadId = report?.chatId;
      if (!threadId || threadsStore.getById(threadId)) return;

      let cancelled = false;
      const markThreadSettled = () => {
        if (!cancelled) setSettledThreadId(threadId);
      };
      void threadsStore
        .ensureThreadIsReady(threadId)
        .then(markThreadSettled, markThreadSettled);

      return () => {
        cancelled = true;
      };
    }, [report?.chatId, threadsStore]);

    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: 0 });
      }
    }, [selectedReport?.referenceId]);

    useMarkReportViewed(selectedReport?.referenceId);

    const currentRoutine = routineStore.getByReferenceId(routineId);

    const isGenerating = routineStore.isOperationPending('generate', routineId);
    const generateReport = () =>
      void routineStore.generateReport(routineId).catch(() => undefined);
    const runAction = currentRoutine
      ? getRoutineActionsWithLatestRun(
          currentRoutine,
          reportStore,
          isGenerating
        )
      : undefined;

    if (isRoutinesLoading || isReportsLoading) {
      return <RoutineContentLoading />;
    }
    if (isRoutinesEmpty || !currentRoutine) return <RoutinesEmpty />;

    const mobileActions = (
      <XStack center className="w-full shrink-0 gap-2 py-2 md:hidden">
        {!currentRoutine.isPaused && runAction?.permissions.canRun && (
          <Button
            variant="outline"
            disabled={!runAction.canRun}
            isLoading={isGenerating}
            tooltip={runAction.runDisabledReason ?? 'Generate a new report'}
            onClick={generateReport}
          >
            Run
          </Button>
        )}
        <BottomSheet.Trigger sheet="routines-history" asChild>
          <Button variant="outline" className="gap-1.5">
            <Icons.history className="size-3.5" />
            View runs
          </Button>
        </BottomSheet.Trigger>
      </XStack>
    );
    const isRoutineThreadLoading =
      !!report?.chatId && !reportThread && settledThreadId !== report.chatId;
    const isContentLoading = shouldShowContentLoader || isRoutineThreadLoading;
    // A run that waits shows what it waits on, not its live chat
    const isReportWaiting =
      !!report && (isRunWaiting(report) || getRunUserWait(report) !== null);
    const liveThreadId =
      isContentLoading || isReportWaiting ? null : reportThread?.id;

    return (
      <FilePreviewHostContext.Provider value={liveThreadId ?? null}>
        <XStack
          ref={routineRowRef}
          className="relative size-full min-w-0 overflow-hidden"
        >
          <YStack className="min-h-0 min-w-0 flex-1 bg-transparent">
            {liveThreadId && report ? (
              <>
                <RoutineThreadContent
                  key={report.referenceId}
                  threadId={liveThreadId}
                  routineId={routineId}
                  report={report}
                  reportBody={selectedReportBody}
                  view={view}
                  onViewChange={onViewChange}
                />
                {mobileActions}
              </>
            ) : (
              <ScrollMask
                ref={scrollRef}
                className="flex-1 overflow-y-auto scrollbar-hide"
              >
                <YStack
                  className={cn(
                    'mx-auto min-h-full w-full justify-start gap-8',
                    isContentLoading ||
                      selectedReportBody.isCanvas ||
                      selectedReportBody.layout === 'pending'
                      ? 'max-w-none px-5 pb-8 pt-0 md:px-8 md:pb-12'
                      : cn(uiStore.chatWidth, 'px-3 py-8 md:px-12 md:py-12')
                  )}
                >
                  {routineReports?.length ? (
                    report ? (
                      isContentLoading ? (
                        <RoutineContentLoading />
                      ) : (
                        <RoutineReportContent
                          routineId={routineId}
                          report={report}
                          reportBody={selectedReportBody}
                        />
                      )
                    ) : null
                  ) : (
                    <YStack
                      center
                      className="mx-auto max-w-md gap-2 py-16 text-center"
                    >
                      <span className="inline-flex size-9 items-center justify-center rounded-full bg-secondary">
                        <Icons.routines className="size-4 text-foreground-subtle" />
                      </span>
                      <Text className="text-sm font-medium">
                        No automated runs yet
                      </Text>
                      <Text className="text-xs text-foreground-muted">
                        This routine will appear here as a conversation after
                        its first scheduled run.
                      </Text>
                    </YStack>
                  )}

                  {mobileActions}
                </YStack>
              </ScrollMask>
            )}
          </YStack>

          {liveThreadId && (
            <>
              <FilePreviewSidebar chatId={liveThreadId} />
              <SandboxTerminalPanel chatId={liveThreadId} overlay />
              <IncludeSelectionButton
                visible={!uiStore.isDraggingFile}
                threadId={liveThreadId}
              />
            </>
          )}

          <BottomSheet.Sheet name="routines-history" ariaLabel="Routine runs">
            <BottomSheet.Header>
              <BottomSheet.Title>Runs</BottomSheet.Title>
            </BottomSheet.Header>
            <ReportList
              reports={routineReports ?? []}
              routineId={routineId}
              allowManualRun={Boolean(runAction?.permissions.canRun)}
              className="mx-auto max-w-lg px-4 pb-4"
            />
          </BottomSheet.Sheet>
        </XStack>
      </FilePreviewHostContext.Provider>
    );
  }
);

type RoutineThreadContentProps = {
  threadId: string;
  routineId: number;
  report: Report;
  reportBody: ReportBody;
  view: RoutineChatView;
  onViewChange?: (view: RoutineChatView) => void;
};

const RoutineThreadContent = ({
  threadId,
  routineId,
  report,
  reportBody,
  view,
  onViewChange,
}: RoutineThreadContentProps) => {
  const showCanvas = reportBody.isCanvas && view === 'canvas';
  const collapseComposer = showCanvas || !reportBody.isCanvas;
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const ranWithout = report.ranWithout;
  const replaceRoutinePrompt = useCallback(
    () =>
      !showCanvas &&
      reportBody.isCanvas &&
      reportBody.artifact.status === 'ready' &&
      onViewChange ? (
        <div
          className={cn(
            'mx-auto w-full translate-x-[4px] transform-gpu px-3 pb-1 md:px-12',
            uiStore.chatWidth
          )}
        >
          <RoutineCanvasLauncher
            file={reportBody.artifact.file}
            onOpen={() => onViewChange('canvas')}
          />
        </div>
      ) : null,
    [onViewChange, reportBody, showCanvas]
  );
  const backend = report.backend;
  // After the run's first reply: the devices it ran without, and, for internal users, the backend that made it
  const afterFirstResponse = useMemo(
    () =>
      !showCanvas && (
        <div
          className={cn(
            'mx-auto w-full translate-x-[4px] transform-gpu px-3 md:px-12',
            uiStore.chatWidth
          )}
        >
          {ranWithout.length > 0 && (
            <div className="pt-4">
              <SkippedDevicesNote devices={ranWithout} />
            </div>
          )}
          <StaffBackendLabel
            backend={backend}
            className="block pt-2 text-xs text-foreground-muted"
          />
        </div>
      ),
    [backend, ranWithout, showCanvas]
  );

  useCommandBarActions(routineChatActions, [], { threadId });

  useLayoutEffect(() => {
    setIsCanvasReady(false);
  }, [showCanvas]);

  return (
    <Thread
      threadId={threadId}
      showChatBanners={false}
      composerPlaceholder="Continue conversation"
      hideFirstTurnActivity
      wrapFirstMessage={replaceRoutinePrompt}
      afterFirstResponse={afterFirstResponse}
      composerInitiallyCollapsed={
        collapseComposer
          ? {
              collapsedHeight: 51,
              collapsedVariant: showCanvas ? 'translucent' : 'card',
            }
          : undefined
      }
      composerVisible={showCanvas ? isCanvasReady : undefined}
      hideCanvasAttachments={!showCanvas && reportBody.isCanvas}
      content={
        showCanvas ? (
          <ScrollMask className="h-full overflow-y-auto scrollbar-hide">
            <YStack className="mx-auto min-h-full w-full max-w-none justify-start px-5 pb-16 pt-0 md:px-8 md:pb-20">
              <RoutineReportContent
                routineId={routineId}
                report={report}
                reportBody={reportBody}
                onCanvasReadyChange={setIsCanvasReady}
              />
            </YStack>
          </ScrollMask>
        ) : undefined
      }
      onComposerExpanded={showCanvas ? () => onViewChange?.('chat') : undefined}
    />
  );
};

const RoutineCanvasLauncher = ({
  file,
  onOpen,
}: {
  file: File;
  onOpen: () => void;
}) => {
  const { status, text, canvas, title, versionKey } = useArtifactContent(file, {
    surface: 'card',
    enabled: true,
  });
  const { isExporting, runExport } = useCanvasExport(file);

  return (
    <div className="mb-3">
      <div className="group relative mt-2 flex h-14 items-center overflow-hidden rounded-xl border border-border-strong bg-surface-active pl-[3.75rem] pr-2 text-left transition-colors smoothing-default hover:bg-surface-hover">
        <button
          type="button"
          onClick={onOpen}
          aria-label="Open full-screen Canvas"
          className="absolute inset-0 z-10 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strongest"
        />
        <div
          aria-hidden
          inert
          className="pointer-events-none absolute inset-y-0 left-0 w-14 bg-surface-active p-1.5 transition-colors group-hover:bg-surface-hover"
        >
          <div className="relative size-full overflow-hidden rounded-md">
            {status === 'ready' ? (
              <div className="size-full animate-in fade-in duration-[50ms]">
                {renderPaperChild(
                  'canvas',
                  {
                    text,
                    url: '',
                    versionKey,
                    canvasParseResult: canvas,
                  },
                  'absolute left-0 top-0 h-[900px] w-[400%] origin-top-left scale-[0.25] border-0'
                )}
              </div>
            ) : (
              <Skeleton className="size-full rounded-none" />
            )}
          </div>
        </div>
        <div className="pointer-events-none relative min-w-0">
          <p className="truncate text-sm font-medium">{title ?? 'Canvas'}</p>
          <p className="text-xs text-foreground-muted">
            <span className="group-hover:hidden">Canvas</span>
            <span className="hidden group-hover:inline">Open preview</span>
          </p>
        </div>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="Canvas actions"
              className="relative z-20 ml-auto flex size-8 shrink-0 items-center justify-center rounded-md text-foreground-muted hover:bg-surface-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strongest"
            >
              <Icons.moreHorizontal strokeWidth={1.5} className="size-4" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end" fit>
            <DropdownMenu.Item onSelect={onOpen}>
              <Icons.eye className="size-3.5" />
              Preview
            </DropdownMenu.Item>
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger disabled={isExporting}>
                <Icons.download />
                Download
              </DropdownMenu.SubTrigger>
              <DropdownMenu.SubContent>
                <DropdownMenu.Label>Appearance</DropdownMenu.Label>
                <DropdownMenu.Separator />
                <DropdownMenu.Item
                  onSelect={() => void runExport('png', 'light')}
                >
                  Light
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={() => void runExport('png', 'dark')}
                >
                  Dark
                </DropdownMenu.Item>
              </DropdownMenu.SubContent>
            </DropdownMenu.Sub>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </div>
  );
};

type RoutineRunsSidebarProps = {
  routineId: number;
  configurationOpen: boolean;
  onConfigurationOpenChange: (open: boolean) => void;
  onImproveRoutineOpen: (reportId: number) => void;
};

export const RoutineRunsSidebar = observer((props: RoutineRunsSidebarProps) => {
  const {
    routineId,
    configurationOpen,
    onConfigurationOpenChange,
    onImproveRoutineOpen,
  } = props;
  const {
    drafts: draftsStore,
    threads: threadsStore,
    reports: reportStore,
    routines: routineStore,
  } = useStores();
  useRoutineRefresh();
  const showHistory = routinesStore.useShowHistory();
  const isRoutinesLoading = !routineReadState.hasLoadedRoutines;
  const isReportsLoading = !routineReadState.hasLoadedReports;
  const isGenerating = routineStore.isOperationPending('generate', routineId);
  const generateReport = () =>
    void routineStore.generateReport(routineId).catch(() => undefined);
  const resumeRoutine = useResumeRoutine();
  const isResuming = routineStore.isOperationPending('resume', routineId);
  const isPausing = routineStore.isOperationPending('pause', routineId);
  const pauseRoutine = (id: number) =>
    void routineStore.pauseRoutine(id).catch(() => undefined);

  useEffect(() => {
    routinesStore.syncSidebarRoutine(routineId);
  }, [routineId]);

  const currentRoutine = routineStore.getByReferenceId(routineId);
  const filteredReports = reportStore.orderedReports.filter(
    (report) => report.reportTemplateId === routineId
  );
  const latestCompletedRun = findLatestCompletedRoutineRun(
    filteredReports.map((report) => adaptReportToRoutineRun(report))
  );
  const improveAction = currentRoutine
    ? getRoutineActions(
        currentRoutine,
        isReportsLoading ? undefined : (latestCompletedRun ?? null)
      )
    : undefined;
  const latestReportId = improveAction?.improveRunId;
  const latestThreadId = improveAction?.improveThreadId;
  const improveRoutineDisabledReason = improveAction?.improveDisabledReason;
  const handleImproveRoutine = useCallback(async () => {
    if (!latestReportId || !latestThreadId) {
      toast.info('Complete a run before improving this routine');
      return;
    }

    try {
      if (!draftsStore.getDraft(latestThreadId)?.content.trim()) {
        await draftsStore.updateDraft({
          draftId: latestThreadId,
          content: IMPROVE_ROUTINE_PROMPT,
        });
      }
      onImproveRoutineOpen(latestReportId);
      await threadsStore.ensureThreadIsReady(latestThreadId);
    } catch {
      toast.error('Could not start routine improvement');
    }
  }, [
    draftsStore,
    latestReportId,
    latestThreadId,
    onImproveRoutineOpen,
    threadsStore,
  ]);

  if (!showHistory) return null;

  if (!currentRoutine && !isRoutinesLoading && !isReportsLoading) {
    return null;
  }

  const actions = currentRoutine
    ? getRoutineActionsWithLatestRun(currentRoutine, reportStore, isGenerating)
    : undefined;
  return (
    <>
      {currentRoutine && actions?.canConfigure && (
        <RoutineConfigureModal
          routine={currentRoutine}
          closeOnNavigate={false}
          open={configurationOpen}
          onClose={() => onConfigurationOpenChange(false)}
          onImproveRoutine={() => void handleImproveRoutine()}
          improveRoutineDisabledReason={improveRoutineDisabledReason}
        />
      )}
      <aside className="hidden h-full w-[25rem] shrink-0 flex-col border-l border-border bg-surface md:flex">
        <XStack className="h-[var(--panel-header-height)] shrink-0 items-center justify-between gap-2 px-4">
          <XStack className="min-w-0 flex-1 items-center gap-2">
            {currentRoutine && (
              <XStack className="shrink-0 items-center gap-px">
                {actions?.canConfigure && (
                  <IconButton
                    size="sm"
                    icon={Icons.slidersHorizontal}
                    tooltip="Configure"
                    aria-label="Configure routine"
                    onClick={() => onConfigurationOpenChange(true)}
                  />
                )}
                {currentRoutine.isAdminPaused ? (
                  <Button size="sm" className="shrink-0 text-xs" disabled>
                    Paused by admin
                  </Button>
                ) : actions?.canResume ? (
                  <IconButton
                    id="resume-routine-button"
                    variant="ghost"
                    size="sm"
                    icon={Icons.playAlt}
                    isLoading={isResuming}
                    tooltip="Resume"
                    aria-label="Resume"
                    onClick={() => void resumeRoutine(routineId)}
                  />
                ) : !currentRoutine.isPaused && actions?.permissions.canRun ? (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    icon={Icons.playAlt}
                    disabled={!actions.canRun}
                    isLoading={isGenerating}
                    tooltip={actions.runDisabledReason ?? 'Run'}
                    aria-label="Run"
                    onClick={generateReport}
                  />
                ) : actions?.canPause ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    disabled={isPausing}
                    onClick={() => pauseRoutine(routineId)}
                  >
                    <Icons.pause className="size-3" />
                    Pause
                  </Button>
                ) : null}
              </XStack>
            )}
          </XStack>
          <XStack className="shrink-0 items-center gap-1">
            <IconButton
              size="sm"
              icon={Icons.panelRightClose}
              scale={1.1}
              strokeWidth={1}
              tooltip="Toggle side panel"
              aria-label="Hide panel"
              onClick={() => routinesStore.setShowHistory(false)}
            />
          </XStack>
        </XStack>

        <YStack className="min-h-0 flex-1 px-4">
          <YStack className="min-h-0 flex-1 gap-3 pt-2">
            {!currentRoutine ? (
              <ReportListLoading />
            ) : (
              <ReportList
                reports={filteredReports}
                routineId={routineId}
                allowManualRun={Boolean(actions?.permissions.canRun)}
              />
            )}
          </YStack>
        </YStack>
      </aside>
    </>
  );
});

const ReportListLoading = observer(() => {
  const showHistory = routinesStore.useShowHistory();

  if (!showHistory) return null;

  return (
    <YStack className="w-full max-w-56 h-full gap-2">
      {[...Array(3)].map((_, index) => (
        <YStack
          className={cn('gap-1 px-3 py-2', 'rounded-lg border border-border')}
          key={index}
        >
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-48" />
        </YStack>
      ))}
    </YStack>
  );
});

const RoutinesEmpty = () => (
  <YStack className="gap-4 p-4">
    <RoutineLibrarySection />
  </YStack>
);

type ReportListProps = {
  reports: Report[];
  routineId: number;
  allowManualRun: boolean;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export const ReportList = observer(
  ({ reports, routineId, allowManualRun, className }: ReportListProps) => {
    const navigate = useNavigate();
    const selectedReportId = routinesStore.useSelectedReportId(routineId);
    const showHistory = routinesStore.useShowHistory();
    const { reports: reportStore, routines: routineStore } = useStores();
    const isGenerating = routineStore.isOperationPending('generate', routineId);
    const isManaged =
      routineStore.getByReferenceId(routineId)?.isManaged ?? false;
    const generateReport = () =>
      void routineStore.generateReport(routineId).catch(() => undefined);

    if (!showHistory) return null;

    const handleDeleteReport = async (report: Report) => {
      // Read before the delete, because the pre-delete order is what says which
      // run to select next. Shared with mobile so the two cannot drift
      const nextReportId = selectRunIdAfterDeletion(
        reports.map((candidate) => candidate.referenceId),
        report.referenceId
      );

      try {
        await reportStore.deleteReport(report.referenceId);
        // Deleting the pinned run repins its neighbour; an unpinned selection
        // just follows the new newest run
        const isPinnedRunDeleted =
          routinesStore.getSelectedReportId(routineId) === report.referenceId &&
          routinesStore.getIsReportPinned(routineId);
        if (isPinnedRunDeleted && nextReportId !== null) {
          routinesStore.pinReport(routineId, nextReportId);
        }
        toast.success('Run deleted');
      } catch (error) {
        toast.error('Failed to delete run', {
          description: error instanceof Error ? error.message : undefined,
        });
      }
    };

    return (
      <YStack className={cn('min-h-0 h-full w-full', className)}>
        {reports.length === 0 ? (
          <Empty.Root
            variant="plain"
            offset="sm"
            className="min-h-[12.5rem] flex-1 p-0"
          >
            <Empty.Header>
              <Empty.Media variant="translucentIcon">
                <Icons.history strokeWidth={1.25} />
              </Empty.Media>
              <Empty.Title className="text-sm">No runs yet</Empty.Title>
              <Empty.Description className="max-w-56 text-xs">
                {isManaged
                  ? 'Managed by Littlebird. Runs on its own schedule.'
                  : allowManualRun
                    ? 'Run this routine now or wait for its next scheduled run.'
                    : 'This routine will run at its next scheduled time.'}
              </Empty.Description>
            </Empty.Header>
            {allowManualRun && (
              <Empty.Actions>
                <Button
                  size="sm"
                  isLoading={isGenerating}
                  onClick={generateReport}
                >
                  <Icons.playAlt className="size-3" />
                  Run
                </Button>
              </Empty.Actions>
            )}
          </Empty.Root>
        ) : (
          <>
            <ScrollMask
              showEndFade
              className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto scrollbar-hide"
            >
              {reports.map((report) => (
                <ReportListItem
                  key={report.referenceId}
                  report={report}
                  selected={selectedReportId === report.referenceId}
                  onClick={() => {
                    void navigate({
                      to: '/routines/$routineId',
                      params: { routineId: String(routineId) },
                      search: (previous) => ({
                        ...previous,
                        run: report.referenceId,
                      }),
                    });
                  }}
                  onDelete={() => void handleDeleteReport(report)}
                />
              ))}
              <OlderRunsLoader routineId={routineId} />
            </ScrollMask>
          </>
        )}
      </YStack>
    );
  }
);

const formatRunListDate = (date: Date) =>
  formatNoteDate(date).replace(/, [^,]+$/, '');

type ReportListItemProps = {
  report: Report;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
};
const ReportListItem = observer(
  ({ report, selected, onClick, onDelete }: ReportListItemProps) => {
    void settingsStore.timeFormat;
    const { reports: reportStore } = useStores();

    const { threads } = useStores();
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const status = parseStatus(report.text.trim());
    const runTitle = report.chatId
      ? threads.getById(report.chatId)?.title
      : undefined;

    useEffect(() => {
      if (!report.chatId || runTitle) return;
      void threads.ensureLoaded(report.chatId).catch(() => undefined);
    }, [report.chatId, runTitle, threads]);

    const isWaiting = isRunWaiting(report);
    const isFinishing =
      isWaiting &&
      reportStore.isOperationPending(
        'continueWithoutDevice',
        report.referenceId
      );
    const userWait = getRunUserWait(report);

    const getStatusContent = () => {
      if (status === ReportStatus.NO_DATA) {
        return {
          icon: <Icons.noteError className="size-2.5 text-foreground-muted" />,
          text: 'Insufficient context',
          isShimmering: false,
        };
      }

      if (userWait) {
        return {
          icon: null,
          text: formatUserWaitTitle(userWait),
          isShimmering: false,
        };
      }

      if (isWaiting) {
        return {
          icon: (
            <DeviceIcon
              platform={report.waitingOn[0].platform}
              className="size-2.5 text-foreground-muted"
            />
          ),
          text: isFinishing
            ? `Finishing without ${devicesWord(report.waitingOn)}…`
            : `Waiting for ${devicesWord(report.waitingOn)}`,
          isShimmering: isFinishing,
        };
      }

      if (status === ReportStatus.GENERATING) {
        return {
          icon: null,
          text: 'Running now',
          isShimmering: true,
        };
      }

      return {
        icon: null,
        text: runTitle ?? 'Run completed',
        isShimmering: false,
      };
    };

    const statusContent = getStatusContent();
    const hasSkippedDevices =
      report.ranWithout.length > 0 && status !== ReportStatus.GENERATING;

    return (
      <ContextMenu
        onOpenChange={(open) => {
          setContextMenuOpen(open);
          if (!open) setConfirmDelete(false);
        }}
      >
        <ContextMenuTrigger asChild>
          <YStack
            onClick={onClick}
            onPointerEnter={() => {
              if (hasRoutineReportDetail(report)) return;
              void reportStore.fetchByReferenceId(report.referenceId);
            }}
            className={cn(
              'group/run-row gap-0.5 rounded-lg px-3 py-2.5',
              selected ? 'bg-surface-active' : 'hover:bg-surface-hover'
            )}
          >
            <XStack className="min-w-0 items-center justify-between">
              <XStack className="min-w-0 flex-1 items-center gap-1.5 no-record">
                {statusContent.icon}
                <p
                  className={cn(
                    'min-w-0 truncate text-sm font-medium',
                    statusContent.isShimmering
                      ? [
                          'bg-clip-text text-transparent',
                          ...SHIMMER_TEXT_CLASSES,
                        ]
                      : selected
                        ? 'text-foreground'
                        : 'text-foreground-subtle group-hover/run-row:text-foreground'
                  )}
                >
                  {statusContent.text}
                </p>
              </XStack>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    aria-label={`More actions for run ${formatNoteDate(
                      report.scheduledAt
                    )}`}
                    onClick={(event) => event.stopPropagation()}
                    className="pointer-events-none flex h-5 w-0 shrink-0 items-center justify-center overflow-hidden text-foreground-muted opacity-0 transition-opacity hover:text-foreground touch:pointer-events-auto touch:ml-2 touch:w-5 touch:opacity-100 group-hover/run-row:ml-2 group-hover/run-row:w-5 group-hover/run-row:pointer-events-auto group-hover/run-row:opacity-100 group-focus-within/run-row:ml-2 group-focus-within/run-row:w-5 group-focus-within/run-row:pointer-events-auto group-focus-within/run-row:opacity-100 data-[state=open]:ml-2 data-[state=open]:w-5 data-[state=open]:pointer-events-auto data-[state=open]:opacity-100"
                  >
                    <Icons.moreHorizontal className="size-4" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  align="end"
                  fit
                  onClick={(event) => event.stopPropagation()}
                >
                  <DropdownMenu.ItemWithConfirm
                    onConfirm={onDelete}
                    icon={Icons.trash}
                    ariaLabel={`Delete run ${formatNoteDate(
                      report.scheduledAt
                    )}`}
                  >
                    Delete
                  </DropdownMenu.ItemWithConfirm>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </XStack>
            <XStack className="min-w-0 items-center justify-between gap-2 text-xs text-foreground-muted">
              <p className="truncate">
                {formatRunListDate(report.scheduledAt)}
                {isWaiting && !isFinishing && ' · Waiting'}
                {hasSkippedDevices &&
                  ` · Skipped ${deviceCount(report.ranWithout.length)}`}
              </p>
              <StaffBackendLabel backend={report.backend} />
            </XStack>
          </YStack>
        </ContextMenuTrigger>
        {contextMenuOpen && (
          <ContextMenuContent
            role="menu"
            aria-label={`Context menu for run ${formatNoteDate(
              report.scheduledAt
            )}`}
          >
            <ContextMenuItem
              onClick={(event) => {
                if (!confirmDelete) {
                  event.preventDefault();
                  event.stopPropagation();
                  setConfirmDelete(true);
                  return;
                }

                onDelete();
              }}
              className={confirmDelete ? 'text-red-400' : 'hover:text-red-400'}
              role="menuitem"
              aria-label={`Delete run ${formatNoteDate(report.scheduledAt)}`}
              icon={confirmDelete ? Icons.check : Icons.trash}
              iconClassName={
                confirmDelete
                  ? 'motion-scale-in-50 motion-duration-200'
                  : undefined
              }
            >
              {confirmDelete ? (
                <span className="motion-opacity-in-0 motion-duration-100">
                  Confirm?
                </span>
              ) : (
                'Delete'
              )}
            </ContextMenuItem>
          </ContextMenuContent>
        )}
      </ContextMenu>
    );
  }
);
